import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';

import { Button, Dialog, DialogButton, Text } from '@/components/ui';
import { planCategoryImport } from '@/lib/finance/import';
import { createCategory, deleteCategory, fetchCategories, fetchCategoryUsage, updateCategory } from '@/lib/finance/api';
import { categoryDeleteWarning } from '@/lib/finance/budget-rows';
import {
  FINANCE_CATEGORY_KINDS,
  type Category,
  type FinanceCategoryKind,
} from '@/lib/finance/types';

function sortCategories(list: Category[]): Category[] {
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function KindSelect({
  value,
  onChange,
}: {
  value: FinanceCategoryKind;
  onChange: (kind: FinanceCategoryKind) => void;
}) {
  return (
    <select
      className="cs-input h-8 w-32 shrink-0 py-0 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value as FinanceCategoryKind)}
    >
      {FINANCE_CATEGORY_KINDS.map((kind) => (
        <option key={kind} value={kind}>
          {kind}
        </option>
      ))}
    </select>
  );
}

export function CategoriesEditor() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newGroup, setNewGroup] = useState('');
  const [newGroupKind, setNewGroupKind] = useState<FinanceCategoryKind>('expense');
  const [childDrafts, setChildDrafts] = useState<Record<number, string>>({});

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const importPlan = useMemo(() => planCategoryImport(importText, categories), [importText, categories]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCategories(await fetchCategories());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setBusy(false);
    }
  }

  const parents = sortCategories(categories.filter((c) => c.parentId == null));
  const childrenOf = (parentId: number) =>
    sortCategories(categories.filter((c) => c.parentId === parentId));

  async function onAddGroup() {
    const name = newGroup.trim();
    if (!name) return;
    await run(async () => {
      await createCategory({ name, kind: newGroupKind, sortOrder: parents.length });
      setNewGroup('');
    });
  }

  async function onAddChild(parent: Category) {
    const name = (childDrafts[parent.id] ?? '').trim();
    if (!name) return;
    await run(async () => {
      await createCategory({
        name,
        parentId: parent.id,
        kind: parent.kind,
        sortOrder: childrenOf(parent.id).length,
      });
      setChildDrafts((d) => ({ ...d, [parent.id]: '' }));
    });
  }

  async function onReorder(siblings: Category[], index: number, dir: -1 | 1) {
    const target = siblings[index];
    const swap = siblings[index + dir];
    if (!target || !swap) return;
    await run(async () => {
      await updateCategory(target.id, { sortOrder: swap.sortOrder });
      await updateCategory(swap.id, { sortOrder: target.sortOrder });
    });
  }

  async function onDelete(category: Category) {
    try {
      const usage = await fetchCategoryUsage(category.id);
      if (usage.childCount > 0) {
        setError(
          `Can't delete "${category.name}" — it still has ${usage.childCount} subcategor${
            usage.childCount === 1 ? 'y' : 'ies'
          }. Remove those first.`,
        );
        return;
      }
      if (!window.confirm(categoryDeleteWarning(category.name, usage.transactionCount))) return;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check category');
      return;
    }
    await run(() => deleteCategory(category.id));
  }

  async function runImport() {
    if (importPlan.newParents.length === 0 && importPlan.newChildren.length === 0) {
      setImportError('Nothing new to import — all categories already exist.');
      return;
    }
    setImportBusy(true);
    setImportError(null);
    try {
      const parentIdByName = new Map<string, number>();
      for (const c of categories) {
        if (c.parentId == null) parentIdByName.set(c.name.toLowerCase(), c.id);
      }
      let groupOrder = parents.length;
      for (const name of importPlan.newParents) {
        const created = await createCategory({ name, kind: 'expense', sortOrder: groupOrder });
        parentIdByName.set(name.toLowerCase(), created.id);
        groupOrder += 1;
      }
      const childOrder = new Map<string, number>();
      for (const { parent, name } of importPlan.newChildren) {
        const parentKey = parent.toLowerCase();
        const parentId = parentIdByName.get(parentKey);
        if (parentId == null) continue;
        const order = childOrder.get(parentKey) ?? childrenOf(parentId).length;
        childOrder.set(parentKey, order + 1);
        await createCategory({ name, parentId, kind: 'expense', sortOrder: order });
      }
      setImportOpen(false);
      setImportText('');
      await load();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImportBusy(false);
    }
  }

  function CategoryRow({
    category,
    siblings,
    index,
    depth,
  }: {
    category: Category;
    siblings: Category[];
    index: number;
    depth: number;
  }) {
    return (
      <View
        className={`flex-row items-center gap-2 px-3 py-2 ${index > 0 || depth > 0 ? 'border-t border-border' : ''}`}
        style={{ paddingLeft: 12 + depth * 20 }}
      >
        <input
          className="cs-input h-8 max-w-[240px] flex-1 py-0"
          defaultValue={category.name}
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next && next !== category.name) {
              void run(() => updateCategory(category.id, { name: next }).then(() => undefined));
            }
          }}
        />
        <KindSelect
          value={category.kind}
          onChange={(kind) =>
            void run(() => updateCategory(category.id, { kind }).then(() => undefined))
          }
        />
        <label className="flex flex-row items-center gap-1">
          <input
            type="checkbox"
            checked={category.isActive}
            onChange={(e) =>
              void run(() =>
                updateCategory(category.id, { isActive: e.target.checked }).then(() => undefined),
              )
            }
          />
          <span className="cs-text-caption">active</span>
        </label>
        <Pressable disabled={index === 0} onPress={() => void onReorder(siblings, index, -1)}>
          <Text variant="caption" className={index === 0 ? 'text-fg-subtle' : 'text-muted'}>
            ↑
          </Text>
        </Pressable>
        <Pressable
          disabled={index === siblings.length - 1}
          onPress={() => void onReorder(siblings, index, 1)}
        >
          <Text
            variant="caption"
            className={index === siblings.length - 1 ? 'text-fg-subtle' : 'text-muted'}
          >
            ↓
          </Text>
        </Pressable>
        <Pressable onPress={() => void onDelete(category)}>
          <Text variant="caption" className="text-danger">
            Delete
          </Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View>
      <View className="mb-4 flex-row flex-wrap items-center gap-2">
        <input
          className="cs-input h-9 min-w-[200px] flex-1 py-0"
          placeholder="New group (top-level category) — e.g. Groceries, Salary, Transfers"
          value={newGroup}
          onChange={(e) => setNewGroup(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void onAddGroup();
          }}
        />
        <KindSelect value={newGroupKind} onChange={setNewGroupKind} />
        <Button
          label="Add group"
          size="sm"
          variant="primary"
          disabled={busy}
          onPress={() => void onAddGroup()}
        />
        <Button
          label="Import"
          size="sm"
          variant="secondary"
          disabled={busy}
          onPress={() => {
            setImportError(null);
            setImportOpen(true);
          }}
        />
      </View>

      {error ? (
        <Text variant="body-sm" className="mb-3 text-danger">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <ActivityIndicator />
      ) : parents.length === 0 ? (
        <Text variant="body" className="text-muted">
          No categories yet. Add a top-level group to begin.
        </Text>
      ) : (
        <View className="flex-col gap-4">
          {parents.map((parent, parentIndex) => {
            const children = childrenOf(parent.id);
            return (
              <View key={parent.id} className="rounded-lg border border-border">
                <CategoryRow category={parent} siblings={parents} index={parentIndex} depth={0} />
                {children.map((child, childIndex) => (
                  <CategoryRow
                    key={child.id}
                    category={child}
                    siblings={children}
                    index={childIndex}
                    depth={1}
                  />
                ))}
                <View
                  className="flex-row items-center gap-2 border-t border-border px-3 py-2"
                  style={{ paddingLeft: 32 }}
                >
                  <input
                    className="cs-input h-8 max-w-[240px] flex-1 py-0"
                    placeholder="Add subcategory…"
                    value={childDrafts[parent.id] ?? ''}
                    onChange={(e) => setChildDrafts((d) => ({ ...d, [parent.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void onAddChild(parent);
                    }}
                  />
                  <Button
                    label="Add"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onPress={() => void onAddChild(parent)}
                  />
                </View>
              </View>
            );
          })}
        </View>
      )}

      <Dialog
        open={importOpen}
        title="Import categories"
        onClose={() => setImportOpen(false)}
        footer={
          <>
            <DialogButton
              label="Cancel"
              variant="secondary"
              onPress={() => setImportOpen(false)}
            />
            <DialogButton
              label={importBusy ? 'Importing…' : 'Import'}
              variant="primary"
              disabled={importBusy || importPlan.newParents.length + importPlan.newChildren.length === 0}
              onPress={() => void runImport()}
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <span className="cs-text-caption text-muted">
            Paste one category per line, dash-delimited like the spreadsheet (e.g.{' '}
            <span className="font-medium">Housing - Utilities</span>). Bare lines (e.g.{' '}
            <span className="font-medium">Shopping</span>) become top-level groups. New categories
            are created as <span className="font-medium">expense</span> — change the few income ones
            afterwards. Existing categories are skipped.
          </span>
          <textarea
            className="cs-input h-64 font-mono text-sm"
            placeholder={'Housing - Rent/Mortgage\nHousing - Utilities\nFood - Groceries\nShopping'}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
          />
          {importText.trim() ? (
            <span className="cs-text-caption text-muted">
              Will create {importPlan.newParents.length} group
              {importPlan.newParents.length === 1 ? '' : 's'} and {importPlan.newChildren.length}{' '}
              subcategor{importPlan.newChildren.length === 1 ? 'y' : 'ies'}
              {importPlan.duplicates > 0 ? ` · ${importPlan.duplicates} already exist` : ''}.
            </span>
          ) : null}
          {importError ? (
            <span className="cs-text-caption text-danger">{importError}</span>
          ) : null}
        </div>
      </Dialog>
    </View>
  );
}
